import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '../../../environments/environment';
import { AppState } from '../_reducers';
import { UserGroup } from '../_models/userGroup';
import { selectAllUserGroups } from '../_selectors/userGroup.selectors';

@Injectable()
export class UserGroupService {
  private apiBaseUrl = environment.apiBaseUrl;
  private groupList$: Observable<UserGroup[]>;
  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) {
    this.groupList$ = this.store.select(selectAllUserGroups);
  }

  /**
   * Returns the list of user groups from the NgRx store.
   *
   * @returns Observable<UserGroup[]> - An observable emitting the user groups stored locally
   */
  getFromLocalStore(): Observable<UserGroup[]> {
    return this.groupList$;
  }

  /**
   * Fetches all user groups from the backend API.
   *
   * @returns Observable<UserGroup[]> - An observable emitting the list of user groups
   */
  getUserGroups(): Observable<UserGroup[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/userGroups')
      .pipe(map((response) => response));
  }

  /**
   * Creates or updates a user group.
   * - If the user group has an `id`, it will be updated.
   * - Otherwise, a new user group will be created.
   *
   * Removes read-only fields and assigns the selected company before saving.
   *
   * @param userGroupItem - The user group to save
   * @returns Observable<UserGroup> - An observable emitting the saved user group
   */
  saveUserGroup(userGroupItem: UserGroup): Observable<UserGroup> {
    const userGroup = { ...userGroupItem };

    delete userGroup.lastUpdated;
    delete userGroup.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    userGroup.companyId = companySelected ? companySelected.id : null;

    if (userGroup.id) {
      return this.http
        .put<UserGroup>(this.apiBaseUrl + 'api/private/v1/userGroups/' + userGroup.id, userGroup)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<UserGroup>(this.apiBaseUrl + 'api/private/v1/userGroups', userGroup)
        .pipe(map((response) => response));
    }
  }

  /**
   * Deletes a user group by its id.
   *
   * @param id - The id of the user group to delete
   * @returns Observable<UserGroup> - An observable emitting an object containing the deleted group id
   */
  deleteUserGroup(id: number): Observable<UserGroup> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/userGroups/' + id).pipe(
      map(() => {
        return { id } as UserGroup;
      })
    );
  }

  /**
   * Synchronously retrieves a user group by id from the local store.
   *
   * ⚠️ Note: This relies on an immediate subscription and assumes the store
   * has already been populated.
   *
   * @param id - The id of the user group to retrieve
   * @returns UserGroup | undefined - The matching user group or undefined if not found
   */
  getUserGroupById(id: number): UserGroup | undefined {
    let group: UserGroup | undefined;

    this.groupList$
      .subscribe((groups) => {
        group = groups.find((f) => Number(f.id) === id);
      })
      .unsubscribe();

    return group;
  }
}
